import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { useArrivedParcelDetail } from '@/entities/warehouse-arrivals';
import { SplitStatusChip } from '@/shared/ui/chips/SplitStatusChip';

export const PagesWarehouseArrivedParcelMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useArrivedParcelDetail(id);

  if (query.isLoading) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.parcels.viewTitle')}
          showBack
          backTo="/warehouse/arrived-parcels"
        />
        <div className="space-y-3 px-4 py-4 lg:px-6 lg:py-6">
          <div className="h-10 animate-pulse rounded-lg bg-slate-200/70" />
          <div className="h-64 animate-pulse rounded-lg bg-slate-200/60" />
          <div className="flex items-center justify-center pt-4">
            <Spinner size="md" />
          </div>
        </div>
      </>
    );
  }

  const status =
    query.error instanceof AxiosError ? query.error.response?.status : undefined;
  if (status === 404 || (!query.error && !query.data)) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.parcels.viewTitle')}
          showBack
          backTo="/warehouse/arrived-parcels"
        />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            title={t('warehouseArrivals.parcels.notFound')}
            message={t('warehouseArrivals.parcels.notFound')}
          />
        </div>
      </>
    );
  }

  if (query.error || !query.data) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.parcels.viewTitle')}
          showBack
          backTo="/warehouse/arrived-parcels"
        />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={extractErrorMessage(
              query.error,
              t('warehouseArrivals.parcels.loadDetailError'),
            )}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  const data = query.data;

  return (
    <>
      <PageHeader
        title={t('warehouseArrivals.parcels.viewTitle')}
        showBack
        backTo="/warehouse/arrived-parcels"
      />
      <div className="px-4 py-4 lg:px-6 lg:py-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:p-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.parcels.fields.name')}
              </dt>
              <dd className="text-sm font-medium text-ink">{data.name || '—'}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.parcels.fields.trekId')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                <span className="font-mono">{data.trek_id}</span>
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.parcels.fields.pinfl')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                <span className="font-mono">{data.pinfl || '—'}</span>
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.parcels.fields.delivered')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    data.delivered_to_customer
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {data.delivered_to_customer
                    ? t('warehouseArrivals.parcels.delivered.yes')
                    : t('warehouseArrivals.parcels.delivered.no')}
                </span>
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.parcels.fields.split')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                {data.is_split && data.split_status ? (
                  <SplitStatusChip status={data.split_status} />
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
};
